import { FIELD_TYPES } from 'utility/constants';
import { AUTOMATION_STEPS } from 'pages/AutomationEngine/steps';
import {
  CONTENT_TYPE_HEADER,
  CUSTOM_CONTENT_TYPE,
  HTTP_METHODS,
} from './constants';

const isTagList = (tagList) => tagList && Array.isArray(tagList);
const tagMapLabel = (tag) => ({
  ...tag,
  name: tag.name || tag.label,
  id: tag.id || tag.value,
});

export const teamMemberOptionsListMapper = (data) =>
  data.map((tm) => ({
    label: `${tm.full_name} (${tm.email})`,
    value: tm.id,
  }));

export const createWizardState = (step) => {
  if (!step) {
    return {
      type: null,
    };
  }

  switch (step.type) {
    case AUTOMATION_STEPS.http_request.type: {
      const data =
        Object.keys(step.details).length !== 0
          ? step.details
          : step.actionHttpRequest;

      const contentTypeInHeaders =
        data.headers &&
        Object.keys(data.headers).find(
          (key) => key.toLowerCase() === CONTENT_TYPE_HEADER
        );

      const customContentType =
        contentTypeInHeaders && data.headers[contentTypeInHeaders];

      const isMethodWithContent = data.method !== HTTP_METHODS.GET;

      return {
        ...step,
        ...data,
        contentType: isMethodWithContent
          ? data.contentType ??
            (customContentType
              ? CUSTOM_CONTENT_TYPE.custom
              : CUSTOM_CONTENT_TYPE.omit)
          : null,
        contentTypeText: isMethodWithContent ? customContentType : null,
        headers: data.headers
          ? Object.keys(data.headers).reduce(
              (acc, el) =>
                isMethodWithContent && el.toLowerCase() === CONTENT_TYPE_HEADER
                  ? acc
                  : acc.concat({
                      key: el,
                      value: data.headers[el],
                      id: Math.random().toString(),
                    }),
              []
            )
          : [],
      };
    }
    default:
      return {
        type: step.type === 'action' ? null : step.type,
        ...step.details,
        hasError: !!step.hasError,
      };
  }
};

export const createPayload = (values, step) => {
  if (AUTOMATION_STEPS.go_to_automation_step.type === values.type) {
    // In some cases label goes as react.element,
    // we can't save it to store, so simply remove label from payload
    delete values.triggerKey?.label;
    delete values.stepKey?.label;
    return {
      ...step,
      type: values.type,
      details: {
        ...values,
      },
    };
  }
  if (AUTOMATION_STEPS.http_request.type === values.type) {
    return {
      ...step,
      type: values.type,
      details: {
        method: values.method,
        url: values.url,
        body: values.body,
        htmlBody: values?.htmlBody,
        htmlUrl: values?.htmlUrl,
        headers: values.headers.reduce(
          (acc, el) => ({ ...acc, [el.key]: el.value }),
          values.contentType === CUSTOM_CONTENT_TYPE.custom
            ? {
                [CONTENT_TYPE_HEADER]: values.contentTypeText,
              }
            : {}
        ),
        contentType: [
          CUSTOM_CONTENT_TYPE.custom,
          CUSTOM_CONTENT_TYPE.omit,
        ].includes(values.contentType)
          ? null
          : values.contentType,
        encodeMergeFieldsInBody: values.encodeMergeFieldsInBody,
      },
    };
  }
  if (AUTOMATION_STEPS.change_field_value.type === values.type) {
    // tag coming back as label but we need name
    return {
      ...step,
      type: values.type,
      details: {
        ...values,
        fieldsToModify: (values.fieldsToModify || []).map((field) => {
          const { specificFieldValue } = field;
          return {
            ...field,
            specificFieldValue: {
              ...specificFieldValue,
              ...(isTagList(specificFieldValue?.tagsToAdd)
                ? {
                    tagsToAdd: specificFieldValue?.tagsToAdd.map(tagMapLabel),
                  }
                : null),
              ...(isTagList(specificFieldValue?.tagsToRemove)
                ? {
                    tagsToRemove:
                      specificFieldValue?.tagsToRemove.map(tagMapLabel),
                  }
                : null),
            },
          };
        }),
      },
    };
  }
  return {
    ...step,
    type: values.type,
    details: { ...values },
  };
};

const cleanCraftJsContent = (craftJsContent) => {
  const { kzn_filters, ...rest } = craftJsContent ?? {};
  return rest;
};

export const cleanEmailData = (email) => {
  return {
    ...email,
    ...{
      ...(email?.craftJsContent && {
        craftJsContent: cleanCraftJsContent(email.craftJsContent),
      }),
    },
  };
};

export const ACTIONS_ON_FAILURE = {
  NOTIFY_CONTINUE: 'notify_continue',
  NOTIFY_PAUSE: 'notify_pause',
  SILENT_CONTINUE: 'silent_continue',
};

export const getActionOnFailureOptions = (t) => [
  {
    label: t('Continue and Notify on Failure'),
    value: ACTIONS_ON_FAILURE.NOTIFY_CONTINUE,
  },
  {
    label: t('Pause and Notify on Failure'),
    value: ACTIONS_ON_FAILURE.NOTIFY_PAUSE,
  },
  {
    label: t('Continue on Failure and Do Not Notify'),
    value: ACTIONS_ON_FAILURE.SILENT_CONTINUE,
  },
];

export const getActionOnFailureNote = (stepType, t) => {
  switch (stepType) {
    case AUTOMATION_STEPS.start_automation.type:
    case AUTOMATION_STEPS.modify_automation.type:
    case AUTOMATION_STEPS.change_tags.type:
    case AUTOMATION_STEPS.modify_related_entities.type:
      return t(
        'This step has multiple components, so it will only fail if ALL components fail.'
      );
    case AUTOMATION_STEPS.http_request.type:
      return t(
        'Anything other than 200- and 300-class responses will be considered an error.'
      );
    case AUTOMATION_STEPS.send_email.type:
      return t(
        'Emails will be marked as successful if there is an existing email that is not on the suppression list.'
      );
    case AUTOMATION_STEPS.create_related_entity.type:
      return t(
        'This will be marked as a failure if either the creation or agentic workflow execution fails, but will ignore field update failures.'
      );
    case AUTOMATION_STEPS.send_related_contact_email.type:
      return t(
        'Emails will be marked as successful if there is at least one existing email that is not on the suppression list.'
      );
    default:
      return null;
  }
};

// check for checkboxes based on orgFieldType
export const isOrgCheckboxesType = (f) =>
  f?.orgFieldType === FIELD_TYPES.Checkboxes.type;

// check Value Type for Modify Related Entity and Change Field Value
export const checkValueTypeUsesVariable = (type) =>
  type === 'variable' || type === 'entity_variable_field';
